import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakAuthService } from 'src/app/auth/keycloak.service';
import { WorkflowService } from 'src/app/services/workflow/workflow.service';
import {
  GUEST_LINK_TOKEN_KEY,
  clearGuestSession,
  storeGuestSession,
} from 'src/app/services/workflow/guest-session';
import { stripAuthResponseParams } from 'src/app/workflow-deep-link';

type LandingStep = 'validating' | 'request-otp' | 'enter-code' | 'error';

/**
 * The landing page for a workflow link, `/#/workflow/link/<token>`. One URL serves both
 * audiences: a signed-in user is forwarded straight into the item, while a guest with no
 * account verifies by emailed one-time code and is then given a session scoped to that item.
 */
@Component({
  selector: 'app-workflow-link-landing',
  standalone: false,
  templateUrl: './workflow-link-landing.component.html',
  styleUrls: ['./workflow-link-landing.component.css'],
})
export class WorkflowLinkLandingComponent implements OnInit, OnDestroy {
  step: LandingStep = 'validating';
  loading = true;
  errorMessage = '';
  infoMessage = '';

  token = '';
  emailMasked: string | null = null;
  code = '';
  sending = false;
  verifying = false;
  remainingAttempts: number | null = null;

  /** The code is masked like a password, but revealed briefly while typing so it can be checked. */
  codeVisible = false;
  private maskTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly MaskDelayMs = 1200;

  /** Resend stays disabled behind a live countdown until the server's cooldown elapses. The
   *  duration always comes from the API, so the client can never drift from the real throttle. */
  resendCooldownSeconds = 0;
  private cooldownTimer: ReturnType<typeof setInterval> | null = null;
  /** Only used when a throttled response carries no duration at all. */
  private static readonly FallbackCooldownSeconds = 60;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly workflowService: WorkflowService,
    private readonly keycloak: KeycloakAuthService
  ) {}

  ngOnInit(): void {
    // Keycloak's auth response rides in the fragment and lands on the route param.
    this.token = stripAuthResponseParams(this.route.snapshot.paramMap.get('token') ?? '');
    if (!this.token) {
      this.fail('This workflow link is missing its token.');
      return;
    }

    this.workflowService.validateWorkflowLink(this.token).subscribe({
      next: (result) => {
        // The endpoint answers 200 either way; `isValid` carries the verdict.
        if (!result?.isValid || !result.workflowItemId) {
          this.fail(
            result?.message ||
              'This workflow link is no longer valid. It may have expired or already been used.'
          );
          return;
        }

        if (this.keycloak.isAuthenticated()) {
          this.loading = false;
          void this.router.navigate(
            ['/workflow-runtime/instances', result.workflowItemId],
            { replaceUrl: true }
          );
          return;
        }

        if (!result.requiresOtp) {
          this.fail('This link cannot be opened without an account. Please sign in and try again.');
          return;
        }

        // A guest starts over: any session left in this tab is from a different link.
        clearGuestSession();
        sessionStorage.setItem(GUEST_LINK_TOKEN_KEY, this.token);
        this.emailMasked = result.recipientEmailMasked ?? null;
        this.loading = false;
        this.step = 'request-otp';
      },
      error: () => this.fail('This workflow link could not be opened. Please request a new one.'),
    });
  }

  toggleCodeVisible(): void {
    this.clearMaskTimer();
    this.codeVisible = !this.codeVisible;
  }

  /** Reveal what was just typed, then re-mask once the guest pauses. */
  onCodeInput(): void {
    this.errorMessage = '';
    this.codeVisible = true;
    this.clearMaskTimer();
    this.maskTimer = setTimeout(
      () => (this.codeVisible = false),
      WorkflowLinkLandingComponent.MaskDelayMs
    );
  }

  ngOnDestroy(): void {
    this.clearMaskTimer();
    this.clearCooldownTimer();
  }

  private clearMaskTimer(): void {
    if (this.maskTimer) {
      clearTimeout(this.maskTimer);
      this.maskTimer = null;
    }
  }

  get canResend(): boolean {
    return !this.sending && this.resendCooldownSeconds === 0;
  }

  get resendLabel(): string {
    if (this.sending) return 'Sending…';
    return this.resendCooldownSeconds > 0
      ? `Send a new code in ${this.resendCooldownSeconds}s`
      : 'Send a new code';
  }

  sendCode(): void {
    if (!this.canResend) return;
    this.sending = true;
    this.errorMessage = '';

    this.workflowService.requestGuestOtp(this.token).subscribe({
      next: (result) => {
        this.sending = false;
        if (!result?.sent) {
          // A throttle can also come back 200-shaped; treat it as a cooldown, not a failure.
          if (result?.retryAfterSeconds || result?.resendAvailableInSeconds) {
            this.onThrottled(
              result.retryAfterSeconds ?? result.resendAvailableInSeconds ?? 0,
              result.message
            );
            return;
          }
          this.errorMessage = result?.message || 'We could not send a code for this link.';
          return;
        }
        this.emailMasked = result.emailMasked ?? this.emailMasked;
        this.infoMessage = result.message;
        this.code = '';
        this.step = 'enter-code';
        this.startResendCooldown(result.resendAvailableInSeconds ?? 0);
      },
      error: (err) => {
        this.sending = false;
        const retryAfter = this.retryAfterFrom(err);
        if (retryAfter !== null) {
          this.onThrottled(retryAfter, err?.error?.message);
          return;
        }
        this.errorMessage = 'We could not send a code. Please try again.';
      },
    });
  }

  /**
   * The server refused to send because one was issued moments ago. That is not an error the
   * guest can act on — a live code is already in their inbox — so move them to the entry step
   * and run the countdown instead of showing a failure.
   */
  private onThrottled(retryAfterSeconds: number, message?: string): void {
    this.startResendCooldown(retryAfterSeconds);
    this.errorMessage = '';
    this.infoMessage =
      message || `A code was already sent. You can request another in ${this.resendCooldownSeconds}s.`;
    if (this.step === 'request-otp') {
      this.code = '';
      this.step = 'enter-code';
    }
  }

  /** A throttled send answers 429 carrying the wait in the body and the Retry-After header. */
  private retryAfterFrom(err: any): number | null {
    if (err?.status !== 429) return null;
    const candidates = [
      Number(err?.error?.retryAfterSeconds),
      Number(err?.error?.resendAvailableInSeconds),
      Number(err?.headers?.get?.('Retry-After')),
    ];
    const found = candidates.find((n) => Number.isFinite(n) && n > 0);
    return found ?? WorkflowLinkLandingComponent.FallbackCooldownSeconds;
  }

  private startResendCooldown(seconds: number): void {
    this.clearCooldownTimer();
    this.resendCooldownSeconds = Number.isFinite(seconds) ? Math.max(0, Math.ceil(seconds)) : 0;
    if (this.resendCooldownSeconds === 0) return;

    this.cooldownTimer = setInterval(() => {
      this.resendCooldownSeconds = Math.max(0, this.resendCooldownSeconds - 1);
      if (this.resendCooldownSeconds === 0) this.clearCooldownTimer();
    }, 1000);
  }

  private clearCooldownTimer(): void {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  }

  verifyCode(): void {
    if (this.verifying || !this.code.trim()) return;
    this.verifying = true;
    this.errorMessage = '';
    this.infoMessage = '';

    this.workflowService.verifyGuestOtp(this.token, this.code.trim()).subscribe({
      next: (session) => {
        this.verifying = false;
        if (!session?.verified || !session.guestSessionToken || !session.workflowItemId) {
          this.onVerifyFailed(session?.message, session?.remainingAttempts ?? null);
          return;
        }

        storeGuestSession({
          token: session.guestSessionToken,
          workflowItemId: session.workflowItemId,
          expiresAt: session.expiresAt ?? null,
          email: session.email ?? null,
        });

        void this.router.navigate(['/workflow/guest'], { replaceUrl: true });
      },
      error: (err) => {
        this.verifying = false;
        const body = err?.error;
        this.onVerifyFailed(body?.message, body?.remainingAttempts ?? null);
      },
    });
  }

  private onVerifyFailed(message: string | undefined, remaining: number | null): void {
    this.remainingAttempts = remaining;
    this.errorMessage = message || 'That code was not correct. Please try again.';
  }

  private fail(message: string): void {
    this.loading = false;
    this.step = 'error';
    this.errorMessage = message;
  }
}
